import { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faTriangleExclamation, faCircleMinus, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";

import { TargetGauge } from "./targetGauges";
import { GAUGE_ARC, GAUGE_VIEWBOX, gaugeTickPoints } from "./gaugeGeometry";

const STATUS_STYLE = {
  "on-track": { tone: "text-theme-success", label: "On track", icon: faCircleCheck },
  behind: { tone: "text-theme-warning", label: "Behind", icon: faTriangleExclamation },
  "no-target": { tone: "text-theme-text-muted", label: "No target", icon: faCircleMinus },
} as const;

const TRACK_WIDTH = 7;
const OUTLINE_WIDTH = TRACK_WIDTH + 3;
const FILL_WIDTH = 10;

const formatValue = (value: number, isRate: boolean) =>
  isRate ? value.toFixed(1) : Math.round(value).toLocaleString("en-US");

export const GaugeMeter: React.FC<{ isCardFullScreen?: boolean; gauge: TargetGauge; onHide?: () => void }> = ({ isCardFullScreen, gauge, onHide }) => {
  const status = STATUS_STYLE[gauge.status];
  const tick = gauge.expected === null ? null : gaugeTickPoints(gauge.expected);
  const [drawnFill, setDrawnFill] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setDrawnFill(gauge.fill));
    return () => cancelAnimationFrame(frame);
  }, [gauge.fill]);

  return (
    <div className={`flex flex-col items-center justify-center gap-1 p-4 lg:p-5 xl:p-4 2xl:p-5 rounded-xl outline ${status.tone} bg-theme-surface ${isCardFullScreen ? "max-h-[300px]" : "h-full"} w-full group hover:outline-2 transition-colors`}>
      <div className="flex items-center gap-1 w-full">
        <span className="text-xs md:text-lg text-theme-text truncate flex-1 text-left">
          {gauge.label}
        </span>
        {onHide && (
          <Tooltip title={`Hide ${gauge.label}`} placement="top">
            <IconButton size="small" aria-label={`Hide ${gauge.label}`} onClick={onHide} className="!bg-transparent">
              <FontAwesomeIcon icon={faXmarkCircle} className="text-2xl !text-theme-text-muted hover:!text-theme-text transition-colors" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col flex-1 items-center justify-center relative">
        <div className="w-full max-w-[180px]">
          <svg viewBox={`0 0 ${GAUGE_VIEWBOX.width} ${GAUGE_VIEWBOX.height}`} className="w-full" role="presentation">
            <path
              d={GAUGE_ARC}
              data-testid="gauge-outline"
              className={status.tone}
              stroke="currentColor"
              strokeWidth={OUTLINE_WIDTH}
              strokeLinecap="round"
              fill="none"
            />
            <path
              d={GAUGE_ARC}
              data-testid="gauge-track"
              className="text-theme-border"
              stroke="currentColor"
              strokeWidth={TRACK_WIDTH}
              strokeLinecap="round"
              fill="none"
            />
            {gauge.attainment !== null && (
              <path
                d={GAUGE_ARC}
                data-testid="gauge-fill"
                className={`${status.tone} transition-all duration-700 ease-out`}
                stroke="currentColor"
                strokeWidth={FILL_WIDTH}
                strokeLinecap="round"
                fill="none"
                pathLength={1}
                strokeDasharray={1}
                strokeDashoffset={1 - drawnFill}
              />
            )}
            {tick && (
              <line
                x1={tick.x1}
                y1={tick.y1}
                x2={tick.x2}
                y2={tick.y2}
                className="text-theme-text"
                stroke="currentColor"
                strokeWidth={2}
              />
            )}
          </svg>
        </div>

        <div className="absolute inset-x-0 top-[52%] group-hover:top-1/2 -translate-y-1/2 flex flex-col items-center transition-all duration-150 ease-in">
          <span className={`${status.tone} text-2xl md:text-3xl group-hover:text-4xl transition-all font-semibold leading-none`}>
            {gauge.attainment === null ? "-" : `${Math.round(gauge.attainment * 100)}%`}
          </span>
        </div>

        <span className="text-xs md:text-sm text-theme-text-muted text-center">
          {formatValue(gauge.actual, gauge.kind === "rate")}
          {gauge.target === null ? "" : ` / ${formatValue(gauge.target, gauge.kind === "rate")}`}
          {gauge.unit ? ` ${gauge.unit}` : ""}
        </span>

        <span className={`flex items-center gap-1 mt-1 text-xs ${status.tone}`}>
          <FontAwesomeIcon icon={status.icon} />
          {status.label}
        </span>
      </div>
    </div>
  );
};
